import React from "react";
import type { TFunction } from "i18next";
import Text from "@/components/ui/text";
import {
  displayTotalCurrency,
  formatNumber,
  getTermsCurrencySign
} from "@/utils/numbers";
import { useAccountCurrency } from "@/services/hooks/useCurrencyConversion";
import { useCurrencyUsage } from "@/entities/settings/hooks";

export { TERMS_FIELD_SUFFIX, termsFieldName } from "./termsCurrency";

/**
 * `ARR (USD)`. An unresolvable account currency leaves the bare metric name —
 * a wrong currency code on a money column is worse than no code at all.
 */
export const systemCurrencyHeader = (
  label: string,
  accountCurrency: string | null | undefined
): string => (accountCurrency ? `${label} (${accountCurrency})` : label);

/**
 * `ARR (Terms Currency)`. The basis is named rather than the code, because the
 * code differs row by row.
 */
export const termsCurrencyHeader = (label: string, t: TFunction): string =>
  `${label} (${t("tables.termsCurrency")})`;

interface AmountProps {
  amount: number | null | undefined;
}

/**
 * The figure already converted to the account's currency. Every row in the
 * column shares that basis, which is what makes the column's sort and its
 * group subtotals meaningful across a mixed-currency book.
 */
export const SystemCurrencyCell: React.FC<AmountProps> = ({ amount }) => {
  const accountCurrency = useAccountCurrency();
  if (amount == null) return null;
  // The figure is in the account's currency whether or not the setting has
  // resolved, so it is shown either way — but without a symbol until the
  // currency is known, on the same grounds the header stays bare.
  return (
    <Text variant="body">
      {accountCurrency
        ? displayTotalCurrency(amount, false, undefined, accountCurrency)
        : formatNumber(amount, 0, false, false).value}
    </Text>
  );
};

interface TermsAmountProps extends AmountProps {
  currency: string | null | undefined;
}

/** The figure as signed, in the record's own currency. */
export const TermsCurrencyCell: React.FC<TermsAmountProps> = ({
  amount,
  currency
}) => {
  // Read unconditionally — this column sits beside its account-currency twin,
  // and a currency sharing that one's glyph needs a sign that tells them
  // apart. Both halves resolve through getCurrencySignForCode, which is the
  // same-resolver obligation distinguishCurrencySign documents.
  const accountCurrency = useAccountCurrency();
  if (amount == null) return null;
  // A row can carry an amount with no currency recorded against it. Passing
  // undefined here would fall through to the account currency, which is the
  // one thing this column must never claim the figure is in.
  return (
    <Text variant="body">
      {currency
        ? displayTotalCurrency(
            amount,
            false,
            undefined,
            currency,
            getTermsCurrencySign(currency, accountCurrency)
          )
        : formatNumber(amount, 0, false, false).value}
    </Text>
  );
};

export interface TermsColumnsDefault {
  /** Whether the terms columns start checked. */
  visibleByDefault: boolean;
  /**
   * False only while the first currency-usage read is still in flight. A
   * failed read is resolved — it just has no answer, and falls back to "on".
   */
  isResolved: boolean;
}

/**
 * Whether the terms-currency columns start checked in Columns management. An
 * account that has only ever signed in its own currency would see the same
 * figure in both halves of every pair, so they start off for it; a single
 * foreign-currency record turns them on. This is only the starting point — a
 * user who checks one keeps it, and an account that later signs abroad does
 * not have its saved choices rewritten.
 *
 * An unresolved answer counts as "on": withholding a column because a read
 * failed hides data the account may well have.
 *
 * `isResolved` is reported separately because that fallback is only safe once
 * the read has actually finished. Seeding a table's saved column layout while
 * it is still in flight would store the terms columns as visible, and the
 * later "no foreign-currency records" answer cannot undo it — the layout is
 * seeded once and thereafter belongs to the user.
 */
export const useTermsColumnsVisibleByDefault = (): TermsColumnsDefault => {
  const { data, isLoading } = useCurrencyUsage();
  return React.useMemo(
    () => ({
      visibleByDefault: data?.hasNonSystemCurrencyRecords ?? true,
      isResolved: !isLoading
    }),
    [data?.hasNonSystemCurrencyRecords, isLoading]
  );
};
